import { useState, useEffect, useReducer } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";

import { getSocket } from "../../services/socket";
import api from "../../services/api";

import toastError from "../../errors/toastError";

import HeaderManager from "../../components/HeaderTableManager/HeaderTableManager";
import TableManager from "../../components/TableManager/TableManager";
import ConfirmationModal from "../../components/ConfirmationModal";
import UserModal from "../../components/UserModal";
import { i18n } from "../../translate/i18n";

import PencilEditIcon from "../../assets/icon/PencilEditIcon";
import TrashIcon from "../../assets/icon/TrashIcon";
import AddIcon from "../../assets/icon/AddIcon";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  tableContainer: {
    width: '100%',
    maxWidth: '80%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '90%',
    },
    [theme.breakpoints.down('md')]: {
      maxWidth: '90%',
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: '90%'
    },
  },
}));


const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_USERS":
      return {
        ...state,
        data: action.payload.users,
        count: action.payload.count,
        hasMore: action.payload.hasMore
      };

    case "UPDATE_USERS":
      const userExists = state.data.some(user => user.id === action.payload.id);

      return {
        ...state,
        data: userExists
          ? state.data.map(user =>
              user.id === action.payload.id ? action.payload : user
            )
          : [...state.data, action.payload]
      };

    case "DELETE_USER":
      return {
        ...state,
        data: state.data.filter(user => user.id !== action.payload),
        count: state.count - 1
      };

    case "RESET":
      return initialState;

    default:
      return state;
  }
};

const initialState = {
  data: [],
  count: 0,
  hasMore: false
};

const Users = () => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const classes = useStyles();
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, initialState);
  const [loggedInUserId, setLoggedInUserId] = useState(null);
  const [adminCount, setAdminCount] = useState(0);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPage(0);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, page]);

  useEffect(() => {
    const admins = users.data.filter(user => user.profile === 'admin');
    setAdminCount(admins.length);
  }, [users.data]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = getSocket(companyId);

    const handleEventUser = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    }

    socket.on(`company-${companyId}-user`, handleEventUser);
    
    return () => {
      socket.off(`company-${companyId}-user`, handleEventUser);
    };
  }, []);

  useEffect(() => {
    const currentUserId = localStorage.getItem("userId");
    setLoggedInUserId(parseInt(currentUserId));
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/users/", {
        params: { searchParam, pageNumber: page + 1, limit: rowsPerPage },
      });
      dispatch({ type: "LOAD_USERS", payload: data });
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
    fetchUsers();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
    fetchUsers();
  };

  const handleDeleteUser = async (userId) => {
    if (userId === loggedInUserId) {
      toast.error(i18n.t("users.toasts.cantDeleteYourself"));
      return;
    }

    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
      dispatch({ type: "DELETE_USER", payload: userId });
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setConfirmModalOpen(false);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const primaryButtons = [
    { label: i18n.t("users.buttons.add"), onClick: handleOpenUserModal, icon: <AddIcon fill='#000' /> },
  ];

  const columns = [
    { field: 'name', headerName: i18n.t("users.table.name"), headerStyle: { fontWeight: '400' }, width: '28%' },
    { field: 'email', headerName: i18n.t("users.table.email"), headerStyle: { fontWeight: '400' }, width: '28%' },
    { field: 'profile', headerName: i18n.t("users.table.profile"), headerStyle: { fontWeight: '400' }, width: '28%' },
  ];

  const actionButtons = [
    ({ row }) => (
      <IconButton
        size="small"
        onClick={() => handleEditUser(row)}
      >
        <PencilEditIcon fill="#7F7F7F" />
      </IconButton>
    ),
    ({ row }) => (
      <IconButton
        size="small"
        onClick={() => {
          setConfirmModalOpen(true);
          setDeletingUser(row);
        }}
        disabled={row.id === loggedInUserId}
        style={{ color: row.id === loggedInUserId ? 'grey' : 'inherit' }}
      >
        <TrashIcon fill="#7F7F7F" />
      </IconButton>
    ),
  ];

  return (
    <>
      <div className={classes.mainContainer} >
        <ConfirmationModal
          title={deletingUser && `${i18n.t("users.confirmationModal.deleteTitle")} ${deletingUser.name}?`}
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={() => handleDeleteUser(deletingUser.id)}
        >
          {i18n.t("users.confirmationModal.deleteMessage")}
        </ConfirmationModal>
        <UserModal
          open={userModalOpen}
          onClose={handleCloseUserModal}
          aria-labelledby="form-dialog-title"
          userId={selectedUser && selectedUser.id}
          usersLength={users.length}
          adminCount={adminCount}
        />
        <div className={classes.tableContainer}>
          <HeaderManager
            title={i18n.t("users.title")}
            primaryButtons={primaryButtons}
            onSearch={handleSearch}
          />
          <TableManager
            columns={columns}
            data={users.data}
            totalCount={users.count}
            loading={loading}
            actionButtons={actionButtons}
            page={page}
            rowsPerPage={rowsPerPage}
            hasPagination={true}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </div>
      </div>
    </>
  );
};

export default Users;
